import { FC, ReactNode, memo } from 'react';

import clsx from 'clsx';
import { every, get, values } from 'lodash';
import { useFormContext } from 'react-hook-form';

import { CountryList, DittoAlert, WithNestedFormFieldsForSwitch } from '@elements';
import { useComponentProps } from '@elements/adaptive-proposal-form/hooks/useComponentProps';
import {
  Divider,
  Input,
  Text,
  Textarea,
  Select,
  Autocomplete,
  Title,
  MultiSelect,
  Paper,
  TextInput,
  Checkbox,
  NumberInput,
  RadioGroup,
  FlexProps,
  Flex,
  Stack,
  DateInput,
  MonthPickerInput
} from '@ui';

import { AdaptiveFormRows } from './AdaptiveFormRows';
import { WithNestedFormFieldsForCheckbox } from './AdaptiveNestedCheckboxForm';
import { FormController } from './FormController';
import { TextInputFormController } from './TextInputFormController';
import {
  AccordionSection,
  AsyncAutocomplete,
  CardSelect,
  ChipSelect,
  SliderCoverPicker,
  ArrayFormFields,
  HeightPicker,
  MembersAccordion,
  SwitcherQuestion,
  NomineeSelect,
  ColumnsContainer,
  ErrorField,
  PhoneNumberFieldWithContext,
  ButtonSelect,
  CustomSlider,
  CoverPicker,
  MemberDetails
} from './custom-fields';
import classes from './styles/Common.module.css';
import {
  AdaptiveFormFieldConfig,
  AdaptiveFormFieldConfigBase,
  Fields,
  SelectFieldConfig,
  TextFieldConfig,
  TextareaFieldConfig,
  DateFieldConfig,
  MonthFieldConfig,
  SwitchFieldConfig,
  CheckboxConfig,
  RadioGroupFieldConfig,
  ChipSelectFieldConfig,
  ButtonSelectFieldConfig,
  ArrayFormFieldConfig,
  MembersAccordionConfig,
  NomineeSelectConfig,
  HeightPickerFieldConfig,
  SliderCoverPickerFieldConfig,
  CustomSliderFieldConfig,
  CoverPickerFieldConfig,
  CardSelectFieldConfig,
  AccordionSectionFieldConfig,
  CustomFieldConfig,
  Title as TitleFieldConfig,
  SectionTitle as SectionTitleFieldConfig,
  AutoCompleteFieldConfig,
  MultiAutoCompleteFieldConfig,
  AsyncAutoCompleteFieldConfig,
  NumberFieldConfig,
  SectionType,
  ColumnsContainerType,
  SectionContainerType,
  MemberDetailsType,
  DividerType,
  SwitchGroupConfig,
  CheckboxGroupConfig,
  NestableCheckboxConfig,
  FieldErrorConfig,
  Alert,
  CountryListFieldConfig,
  IntlPhoneNumberFieldConfig
} from './types';

export interface AdaptiveFieldByTypeProps {
  config: AdaptiveFormFieldConfig;
  context?: any;
}

// ============================================================================
// Field Renderer Types
// ============================================================================

interface FieldRendererProps<T extends AdaptiveFormFieldConfig> {
  config: T & AdaptiveFormFieldConfigBase;
  context?: any;
}

type FieldRenderer<T extends AdaptiveFormFieldConfig> = FC<FieldRendererProps<T>>;

// ============================================================================
// Shared Utilities
// ============================================================================

const getFlexProps = (config: AdaptiveFormFieldConfig): FlexProps => ({
  flex: 1,
  w: '100%',
  gap: 24,
  direction: 'row',
  justify: 'flex-start',
  ...config.gridProps
});

// ============================================================================
// Field Renderers
// ============================================================================

const SelectFieldRenderer: FieldRenderer<SelectFieldConfig> = memo(({ config }) => {
  const { control } = useFormContext();
  const props = useComponentProps(config);
  const flexProps = getFlexProps(config);

  if (!props?.data && !props?.options) return null;

  return (
    <Flex {...flexProps}>
      <FormController name={config.name} control={control}>
        <Select size="xl" w="100%" label={config.label} withAsterisk {...props} />
      </FormController>
    </Flex>
  );
});

const TextFieldRenderer: FieldRenderer<TextFieldConfig> = memo(({ config }) => {
  const { control } = useFormContext();
  const props = useComponentProps(config) || {};
  const flexProps = getFlexProps(config);

  return (
    <Flex {...flexProps}>
      <TextInputFormController name={config.name} control={control}>
        <TextInput w="100%" size="xl" label={config.label} withAsterisk {...props} />
      </TextInputFormController>
    </Flex>
  );
});

const AutocompleteFieldRenderer: FieldRenderer<AutoCompleteFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!props?.data) return null;

    const { data, ...autoCompleteProps } = props;
    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <Autocomplete
            w="100%"
            label={config.label}
            data={data}
            withAsterisk
            placeholder="Type to search"
            {...autoCompleteProps}
          />
        </FormController>
      </Flex>
    );
  }
);

const MultiSelectAutocompleteFieldRenderer: FieldRenderer<MultiAutoCompleteFieldConfig> =
  memo(({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!props?.data && !props?.options) return null;

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <MultiSelect
            label={config.label}
            withAsterisk
            {...props}
            w="100%"
            size="xl"
          />
        </FormController>
      </Flex>
    );
  });

const AsyncAutocompleteFieldRenderer: FieldRenderer<AsyncAutoCompleteFieldConfig> =
  memo(({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!props?.onSearch) return null;

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <AsyncAutocomplete
            w="100%"
            name={config.name}
            label={config.label}
            withAsterisk
            onSearch={props.onSearch}
            {...props}
          />
        </FormController>
      </Flex>
    );
  });

const NumberFieldRenderer: FieldRenderer<NumberFieldConfig> = memo(({ config }) => {
  const { control } = useFormContext();
  const props = useComponentProps(config) || {};
  const flexProps = getFlexProps(config);

  return (
    <Flex {...flexProps}>
      <FormController name={config.name} control={control}>
        <NumberInput
          w="100%"
          size="xl"
          label={config.label}
          withAsterisk
          {...props}
        />
      </FormController>
    </Flex>
  );
});

const TextareaFieldRenderer: FieldRenderer<TextareaFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config) || {};
    const flexProps = getFlexProps(config);

    return (
      <Flex {...flexProps}>
        <TextInputFormController name={config.name} control={control}>
          <Textarea
            w="100%"
            size="xl"
            label={config.label}
            withAsterisk
            {...props}
          />
        </TextInputFormController>
      </Flex>
    );
  }
);

const DateFieldRenderer: FieldRenderer<DateFieldConfig> = memo(({ config }) => {
  const { control } = useFormContext();
  const props = useComponentProps(config) || {};
  const flexProps = getFlexProps(config);

  return (
    <Flex {...flexProps}>
      <FormController name={config.name} control={control}>
        <DateInput
          type="default"
          size="xl"
          w="100%"
          label={config.label}
          placeholder="DD/MM/YYYY"
          withAsterisk
          clearable
          {...props}
        />
      </FormController>
    </Flex>
  );
});

const MonthFieldRenderer: FieldRenderer<MonthFieldConfig> = memo(({ config }) => {
  const { control } = useFormContext();
  const props = useComponentProps(config) || {};
  const flexProps = getFlexProps(config);

  return (
    <Flex {...flexProps}>
      <FormController name={config.name} control={control}>
        <MonthPickerInput
          size="xl"
          w="100%"
          placeholder="MM/YYYY"
          withAsterisk
          {...props}
          label={config.label as Required<ReactNode>}
        />
      </FormController>
    </Flex>
  );
});

const SwitchFieldRenderer: FieldRenderer<SwitchFieldConfig> = memo(({ config }) => {
  const props = useComponentProps(config);

  if (!props?.options) return null;

  return (
    <WithNestedFormFieldsForSwitch
      config={config}
      card={config.card}
      background={config.background}
      showFieldsByDefault={config.showFieldsByDefault}
    >
      <Flex flex={1} w="100%">
        <SwitcherQuestion name={config.name} label={config.label} {...props} />
      </Flex>
    </WithNestedFormFieldsForSwitch>
  );
});

const CheckboxFieldRenderer: FieldRenderer<CheckboxConfig | NestableCheckboxConfig> =
  memo(({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config) || {};
    const flexProps = getFlexProps(config);

    return (
      <WithNestedFormFieldsForCheckbox config={config as NestableCheckboxConfig}>
        <Flex {...flexProps}>
          <FormController name={config.name} control={control}>
            <Checkbox
              color="primary.4"
              label={config.label}
              defaultChecked={props.defaultChecked}
              {...props}
            />
          </FormController>
        </Flex>
      </WithNestedFormFieldsForCheckbox>
    );
  });

const RadioGroupFieldRenderer: FieldRenderer<RadioGroupFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!config.name || !props?.options) return null;

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <RadioGroup label={config.label} {...props} />
        </FormController>
      </Flex>
    );
  }
);

const IntlPhoneNumberFieldRenderer: FieldRenderer<IntlPhoneNumberFieldConfig> = memo(
  ({ config }) => {
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!props?.mobileFieldName || !props?.countryFieldName) return null;

    return (
      <Flex {...flexProps}>
        <PhoneNumberFieldWithContext label={config.label} withAsterisk {...props} />
      </Flex>
    );
  }
);

const CountryListFieldRenderer: FieldRenderer<CountryListFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config) || {};
    const flexProps = getFlexProps(config);

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <CountryList
            withAsterisk
            {...props}
            label={typeof config.label === 'string' ? config.label : ''}
            placeholder={props.placeholder}
          />
        </FormController>
      </Flex>
    );
  }
);

const ChipSelectFieldRenderer: FieldRenderer<ChipSelectFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!config.name || !props?.data) return null;

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <ChipSelect
            label={typeof config.label === 'string' ? config.label : undefined}
            {...props}
          />
        </FormController>
      </Flex>
    );
  }
);

const ButtonSelectFieldRenderer: FieldRenderer<ButtonSelectFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!config.name || !props?.data) return null;

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <ButtonSelect label={config.label} {...props} />
        </FormController>
      </Flex>
    );
  }
);

const ArrayFormFieldRenderer: FieldRenderer<ArrayFormFieldConfig> = memo(
  ({ config }) => {
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    return (
      <Flex {...flexProps} w={{ base: '100%' }}>
        <ArrayFormFields
          name={config.name}
          {...props}
          formConfig={config.fields}
          subFieldTitle={props?.subFieldTitle ?? ''}
          maxForms={props?.maxForms ?? 5}
        />
      </Flex>
    );
  }
);

const MembersAccordionFieldRenderer: FieldRenderer<MembersAccordionConfig> = memo(
  ({ config }) => {
    const props = useComponentProps(config);

    if (typeof config.getFields !== 'function' || !props?.onSelectKey) return null;
    return (
      <MembersAccordion
        name={config.name}
        label={config.label}
        memberIdsWithDisease={props?.memberIdsWithDisease}
        getSubQuestionsConfig={config.getFields}
        lazyUnmount={props?.lazyUnmount}
        {...props}
        onSelectKey={props?.onSelectKey}
      />
    );
  }
);

const NomineeSelectFieldRenderer: FieldRenderer<NomineeSelectConfig> = memo(
  ({ config }) => {
    const props = useComponentProps(config) || {};
    return (
      <NomineeSelect
        name={config.name}
        label={config.label}
        fields={config.fields}
        {...props}
      />
    );
  }
);

const HeightPickerFieldRenderer: FieldRenderer<HeightPickerFieldConfig> = memo(
  ({ config }) => {
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    return (
      <Flex {...flexProps}>
        <HeightPicker
          label={config.label}
          ftName={config.ftName}
          inName={config.inName}
          withAsterisk
          {...props}
        />
      </Flex>
    );
  }
);

const SliderCoverPickerFieldRenderer: FieldRenderer<SliderCoverPickerFieldConfig> =
  memo(({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!props) {
      return null;
    }

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <SliderCoverPicker label={config.label} {...props} />
        </FormController>
      </Flex>
    );
  });

const CustomSliderFieldRenderer: FieldRenderer<CustomSliderFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);

    if (!props) {
      return null;
    }

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <CustomSlider label={config.label} {...props} />
        </FormController>
      </Flex>
    );
  }
);

const CoverPickerFieldRenderer: FieldRenderer<CoverPickerFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);
    const flexProps = getFlexProps(config);
    if (!props) return null;

    return (
      <Flex {...flexProps}>
        <FormController name={config.name} control={control}>
          <CoverPicker {...props} />
        </FormController>
      </Flex>
    );
  }
);

const CardSelectFieldRenderer: FieldRenderer<CardSelectFieldConfig> = memo(
  ({ config }) => {
    const { control } = useFormContext();
    const props = useComponentProps(config);

    if (!props) return null;

    return (
      <Flex flex={1}>
        <FormController name={config.name} control={control}>
          <CardSelect {...props} />
        </FormController>
      </Flex>
    );
  }
);

const AccordionSectionFieldRenderer: FieldRenderer<AccordionSectionFieldConfig> =
  memo(({ config }) => {
    const props = useComponentProps(config) || {};

    if (!config.fields && !props.renderFormComponent) return null;

    return (
      <AccordionSection
        itemValue={`accordion-${config.name}`}
        {...props}
        formConfig={config.fields}
      />
    );
  });

const SectionFieldRenderer: FieldRenderer<SectionType> = memo(({ config }) => {
  const { title, subtitle } = config.componentProps || {};

  return (
    <Stack w={'100%'} gap={24}>
      <Stack gap={8}>
        <Text fz={18} fw={600} lh={1}>
          {title}
        </Text>
        <Text c="secondary">{subtitle}</Text>
      </Stack>
      <Flex flex={1} direction="column" gap={20}>
        <AdaptiveFormRows config={config.fields} />
      </Flex>
      <Divider w="100%" mt={12} />
    </Stack>
  );
});

const SectionContainerFieldRenderer: FieldRenderer<SectionContainerType> = memo(
  ({ config }) => {
    const { bgVariant, ...restProps } = config.componentProps ?? {};
    return (
      <Paper
        w={'100%'}
        {...restProps}
        className={clsx(
          classes.sectionContainer,
          bgVariant && classes[`sectionContainer_${bgVariant}`]
        )}
      >
        <Flex flex={1} direction="column" gap={20}>
          <AdaptiveFormRows config={config.fields} />
        </Flex>
      </Paper>
    );
  }
);

const ColumnsContainerFieldRenderer: FieldRenderer<ColumnsContainerType> = memo(
  ({ config, context }) => {
    const columns = config.componentProps?.columns || 2;

    return (
      <ColumnsContainer fields={config.fields} columns={columns} context={context} />
    );
  }
);

const DividerFieldRenderer: FieldRenderer<DividerType> = memo(({ config }) => {
  return (
    <Flex flex={1}>
      <Divider w="100%" mt={12} {...config.componentProps} />
    </Flex>
  );
});

const MemberDetailsFieldRenderer: FieldRenderer<MemberDetailsType> = memo(
  ({ config }) => {
    if (!config.componentProps?.member) return null;

    return (
      <Flex flex={1}>
        <MemberDetails {...config.componentProps} />
      </Flex>
    );
  }
);

const TitleFieldRenderer: FieldRenderer<TitleFieldConfig> = memo(({ config }) => {
  const { primary, secondary } = config.componentProps || {};

  return (
    <Flex direction="column" gap="xs" w="100%">
      <Title variant="h6" fz={18} fw={600} lh={1} c="text.6">
        {primary}
      </Title>
      <Text lightColor="secondary.6" darkColor="white.6" fz={13} lh={1.5}>
        {secondary}
      </Text>
    </Flex>
  );
});

const SectionTitleFieldRenderer: FieldRenderer<SectionTitleFieldConfig> = memo(
  ({ config }) => {
    const { primary, secondary, primaryProps, secondaryProps, containerProps } =
      config.componentProps || {};

    return (
      <Flex direction="column" gap="xs" {...containerProps}>
        <Text
          fz={16}
          fw={600}
          lightColor="text.6"
          darkColor="white.6"
          {...primaryProps}
        >
          {primary}
        </Text>
        {!!secondary && (
          <Text fz={14} c="secondary" {...secondaryProps}>
            {secondary}
          </Text>
        )}
      </Flex>
    );
  }
);

const SwitchGroupFieldRenderer: FieldRenderer<SwitchGroupConfig> = memo(
  ({ config }) => {
    const { getValues, formState } = useFormContext();

    if (config.name) {
      return (
        <Flex direction="column" gap={20}>
          <AdaptiveFormRows config={config.fields} />
          {get(formState.errors, config.name) && (
            <Input.Error size="xl">
              {'Please select at least one option'}
            </Input.Error>
          )}
        </Flex>
      );
    }

    const switches = values(config.fields)
      .filter((field: any) => field.type === Fields.switch)
      .map((field: any) => field.name);

    const touchedField = config.componentProps?.touchedField;
    const parentAnswerPath = touchedField?.replace('.details', '.answer');
    const parentAnswer = parentAnswerPath
      ? get(getValues(), parentAnswerPath)
      : undefined;

    const noToAll =
      every(getValues(switches), (v) => !v) &&
      (formState.isSubmitted || get(formState.touchedFields, touchedField || '')) &&
      parentAnswer === true;

    return (
      <Flex direction="column" gap={20} w="100%">
        <AdaptiveFormRows config={config.fields} />
        {noToAll && (
          <Input.Error size="xl">
            At least one option should be selected as Yes
          </Input.Error>
        )}
      </Flex>
    );
  }
);

const CheckboxGroupFieldRenderer: FieldRenderer<CheckboxGroupConfig> = memo(
  ({ config }) => {
    const { getValues, formState } = useFormContext();

    if (config.name) {
      return (
        <Flex direction="column" gap={20}>
          <AdaptiveFormRows config={config.fields} />
          {get(formState.errors, config.name) && (
            <Input.Error size="xl">
              {'Please select at least one option'}
            </Input.Error>
          )}
        </Flex>
      );
    }

    const checkboxes = values(config.fields)
      .filter((field: any) => field.type === Fields.checkbox)
      .map((field: any) => field.name);

    const touchedField = config.componentProps?.touchedField;
    const parentAnswerPath = touchedField?.replace('.details', '.answer');
    const parentAnswer = parentAnswerPath
      ? get(getValues(), parentAnswerPath)
      : undefined;

    const noneSelected =
      every(getValues(checkboxes), (v) => !v) &&
      (formState.isSubmitted || get(formState.touchedFields, touchedField || '')) &&
      parentAnswer === true;

    return (
      <Flex direction="column" gap={20} w="100%">
        <AdaptiveFormRows config={config.fields} />
        {noneSelected && (
          <Input.Error size="xl">At least one option should be selected</Input.Error>
        )}
      </Flex>
    );
  }
);

const ErrorFieldRenderer: FieldRenderer<FieldErrorConfig> = memo(({ config }) => {
  const props = useComponentProps(config);

  return <ErrorField name={config.name} {...props} />;
});

const AlertFieldRenderer: FieldRenderer<Alert> = memo(({ config }) => {
  const props = useComponentProps(config);
  if (!props?.message) return null;

  return (
    <Flex flex={1}>
      <DittoAlert {...props} />
    </Flex>
  );
});

const CustomFieldRenderer: FieldRenderer<CustomFieldConfig> = memo(
  ({ config, context }) => {
    const { Render, componentProps = {} } = config;

    return (
      <Render
        config={config.fields || []}
        context={context}
        componentProps={componentProps}
      />
    );
  }
);

// ============================================================================
// Field Renderer Registry
// ============================================================================

const fieldRenderers: Partial<Record<Fields, FieldRenderer<any>>> = {
  // Form inputs
  [Fields.select]: SelectFieldRenderer,
  [Fields.text]: TextFieldRenderer,
  [Fields.textarea]: TextareaFieldRenderer,
  [Fields.number]: NumberFieldRenderer,
  [Fields.date]: DateFieldRenderer,
  [Fields.month]: MonthFieldRenderer,
  [Fields.checkbox]: CheckboxFieldRenderer,
  [Fields.radioGroup]: RadioGroupFieldRenderer,
  [Fields.intlPhoneNumber]: IntlPhoneNumberFieldRenderer,
  [Fields.countryList]: CountryListFieldRenderer,

  // Autocomplete variants
  [Fields.autocomplete]: AutocompleteFieldRenderer,
  [Fields.multiSelectAutocomplete]: MultiSelectAutocompleteFieldRenderer,
  [Fields.asyncAutocomplete]: AsyncAutocompleteFieldRenderer,

  // Selection components
  [Fields.chipSelect]: ChipSelectFieldRenderer,
  [Fields.buttonSelect]: ButtonSelectFieldRenderer,
  [Fields.cardSelect]: CardSelectFieldRenderer,

  // Pickers
  [Fields.heightPicker]: HeightPickerFieldRenderer,
  [Fields.sliderCoverPicker]: SliderCoverPickerFieldRenderer,
  [Fields.customSlider]: CustomSliderFieldRenderer,
  [Fields.coverPicker]: CoverPickerFieldRenderer,

  // Complex fields
  [Fields.switch]: SwitchFieldRenderer,
  [Fields.arrayForm]: ArrayFormFieldRenderer,
  [Fields.membersAccordion]: MembersAccordionFieldRenderer,
  [Fields.nomineeSelect]: NomineeSelectFieldRenderer,
  [Fields.accordionSection]: AccordionSectionFieldRenderer,
  [Fields.switchGroup]: SwitchGroupFieldRenderer,
  [Fields.checkboxGroup]: CheckboxGroupFieldRenderer,

  // Layout fields
  [Fields.section]: SectionFieldRenderer,
  [Fields.sectionContainer]: SectionContainerFieldRenderer,
  [Fields.columnsContainer]: ColumnsContainerFieldRenderer,
  [Fields.divider]: DividerFieldRenderer,
  [Fields.title]: TitleFieldRenderer,
  [Fields.sectionTitle]: SectionTitleFieldRenderer,
  [Fields.memberDetails]: MemberDetailsFieldRenderer,

  // Special fields
  [Fields.custom]: CustomFieldRenderer,
  [Fields.error]: ErrorFieldRenderer,
  [Fields.alert]: AlertFieldRenderer
};

/**
 * @description Renders different components based on the fieldType property.
 *
 * Supported field types are:
 * autocomplete - src/components/form/AutoComplete.tsx
 * multiSelectAutocomplete - src/components/form/MultiAutoComplete.tsx
 * input - src/components/form/TextInput.tsx
 * switch - src/components/form/SwitcherQuestion.tsx
 *
 */
export const AdaptiveFieldByType = (props: AdaptiveFieldByTypeProps) => {
  const { config: fieldConfig, context } = props;

  // Get the renderer from the registry
  const Renderer = fieldRenderers[fieldConfig.type];

  if (!Renderer) {
    console.warn(`No renderer found for field type: ${fieldConfig.type}`);
    return null;
  }

  return <Renderer config={fieldConfig as any} context={context} />;
};
